import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import EyeIcon from '@assets/icons/admin-center/creator/eye.svg';
import PrinterIcon from '@assets/icons/learning-overview/task/printer.svg';
import { BaseButton, BaseInput, HtmlInjection } from '@components';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useLingui } from '@lingui/react/macro';
import { ROUTES } from '@router/constants.ts';
import { getUserPreferencesOrDefaults } from '@routes/___shared___/utils.ts';
import { TASK_SCALE_TYPE } from '@routes/sidebar/___shared___/constants.ts';
import { Description, Guideline, RatingsWrapper, TaskWrapper } from '@routes/sidebar/___shared___/styled.tsx';
import type { TaskScaleType } from '@routes/sidebar/___shared___/types.ts';
import { getTaskRoute } from '@routes/sidebar/___shared___/utils.ts';
import { getAssessorTask } from '@routes/sidebar/admin-center/assessor/___pages___/evaluate-task/model/evaluateTask.slice.ts';
import {
    getLearnerTask,
    getTaskAssessors,
} from '@routes/sidebar/learning/___pages___/learning-overview/model/task.slice.ts';
import type { AttemptLogStatus } from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/constants.ts';
import {
    ATTEMPT_LOG_STATUS,
    LOG_TITLE_COLOR_MAPPER,
    RATING_BACKGROUNDS,
} from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/constants.ts';
import {
    EvaluationLogSection,
    EvidenceButtonsWrapper,
    EvidenceWrapper,
    Log,
    LogContent,
    LogHeader,
    PageTitle,
    PrintWrapper,
    TaskEvaluationLogWrapper,
    TaskTitle,
} from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/evaluation-log/styled.tsx';
import {
    CommentsContent,
    DateWrapper,
    Label,
    LogInfo,
    LogInfoText,
    LogInfoTextBold,
    LogInfoWrapper,
    Rating,
    ReferenceContentWrapper,
    SectionWrapper,
    StatusInfoWrapper,
    StatusTextWrapper,
} from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/styled.tsx';
import {
    buildEvaluationLogEntries,
    getEvaluationLogEntryViewData,
    getLogPersonText,
} from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/utils.ts';
import { resolveContentUri } from '@shared/model/embededContent.slice.ts';
import { formatDateBasedOnPreferences } from '@utils/date.ts';
import { isContentEntryValid } from '@utils/embededContents.ts';
import { generateUniqueKeys } from '@utils/generators.ts';
import { openInNewTab } from '@utils/navigation.ts';

interface Props {
    isAssessorView: boolean;
}

export const TaskEvaluationLog: React.FC<Props> = ({ isAssessorView }) => {
    const { pathwayId, taskId } = useParams<{
        pathwayId: string;
        taskId: string;
    }>();

    const { t } = useLingui();
    const dispatch = useAppDispatch();

    const userFullInfo = useAppSelector((state) => state.user.userInfo);
    const taskData = useAppSelector((state) =>
        isAssessorView ? state.assessorEvaluateTask.taskData : state.learningTask.taskData,
    );
    const assessors = useAppSelector((state) => state.learningTask.assessors);
    const contentByIdUrl = useAppSelector((state) => state.contentAssets.contentByIdUrl);

    const userPreferencesSettings = getUserPreferencesOrDefaults(userFullInfo);
    const { preferredDateFormat: dateFormat } = userPreferencesSettings;

    const STATUS_TEXT_LABELS = {
        redo: t`Redo`,
        closed: t`Closed`,
        exempted: t`Exempted`,
        exemptionRejected: t`Exemption rejected`,
        submitted: t`Submitted`,
    };

    const getLogStatusText = (status: AttemptLogStatus, attemptNumber: number) => {
        const LOG_STATUS_TEXT_MAPPER: Partial<Record<AttemptLogStatus, string>> = {
            [ATTEMPT_LOG_STATUS.PENDING]: t`Learner Submission (Attempt ${attemptNumber})`,
            [ATTEMPT_LOG_STATUS.PENDING_EXEMPTION]: t`Exemption Requested`,
            [ATTEMPT_LOG_STATUS.RECALLED]: t`Learner Submission (Attempt ${attemptNumber})`,
            [ATTEMPT_LOG_STATUS.REDO_REQUIRED]: t`Assessor Evaluation`,
            [ATTEMPT_LOG_STATUS.CLOSED]: t`Assessor Evaluation`,
            [ATTEMPT_LOG_STATUS.EXEMPTED]: t`Exempted`,
            [ATTEMPT_LOG_STATUS.EXEMPTION_REJECTED]: t`Exemption rejected`,
        };

        return LOG_STATUS_TEXT_MAPPER[status] ?? '';
    };

    const ratingScale = taskData?.taskConfiguration?.taskScaleType;

    const ratingScaleOptions: Record<TaskScaleType, string[]> = {
        [TASK_SCALE_TYPE.TwoPointApprove]: [t`Disapproved`, t`Approved`],
        [TASK_SCALE_TYPE.TwoPointConfidence]: [t`Not Confident`, t`Confident`],
        [TASK_SCALE_TYPE.FivePointCompetence]: [
            t`Unsatisfactory`,
            t`Needs improvement`,
            t`Meets expectations`,
            t`Exceeds expectations`,
            t`Exceptional`,
        ],
    };

    const ratings = ratingScale ? ratingScaleOptions[ratingScale] : undefined;
    const backgrounds = ratingScale ? RATING_BACKGROUNDS[ratingScale] : undefined;

    const evaluationLogEntries = buildEvaluationLogEntries(taskData?.evaluationLog ?? []);

    const handleEvidencePreview = (evidenceFileId: string) => {
        const contentEntry = contentByIdUrl[evidenceFileId];

        if (contentEntry && isContentEntryValid(contentEntry)) {
            openInNewTab(contentEntry.tempUrl);

            return;
        }

        dispatch(resolveContentUri(evidenceFileId))
            .then((res) => {
                openInNewTab(res.url);
            })
            .catch(() => {});
    };

    const handlePrint = () => {
        if (!pathwayId || !taskId) {
            return;
        }

        openInNewTab(
            getTaskRoute(pathwayId, taskId, ROUTES.SIDEBAR.LEARNING_TASK.PRINT_EVALUATION_LOG, isAssessorView),
        );
    };

    useEffect(() => {
        if (!pathwayId || !taskId) {
            return;
        }

        if (isAssessorView) {
            void dispatch(
                getAssessorTask({
                    userPathwayId: Number(pathwayId),
                    pathwayItemId: Number(taskId),
                }),
            );

            return;
        }

        void dispatch(
            getLearnerTask({
                pathwayId,
                pathwayItemId: Number(taskId),
            }),
        );
        void dispatch(getTaskAssessors());
    }, [dispatch, pathwayId, taskId, isAssessorView]);

    return (
        <TaskEvaluationLogWrapper>
            {!!taskData && (
                <>
                    <TaskTitle>{taskData.details.taskName}</TaskTitle>

                    <EvaluationLogSection>
                        <PageTitle>{t`Evaluation log`}</PageTitle>

                        {evaluationLogEntries.map((entry) => {
                            const { attempt, status, isAssessorEvaluation, displayDateUTC, key } = entry;
                            const {
                                showAssessorPerson,
                                showStatusPill,
                                showAssessorComments,
                                statusLabel,
                                StatusIcon,
                                isInactiveRatings,
                                evidenceFileId,
                            } = getEvaluationLogEntryViewData(entry, STATUS_TEXT_LABELS);

                            return (
                                <Log key={key}>
                                    <LogHeader $status={status}>
                                        <LogInfoWrapper>
                                            <LogInfo>
                                                <LogInfoTextBold $color={LOG_TITLE_COLOR_MAPPER[status]}>
                                                    {getLogStatusText(status, attempt.number)}
                                                </LogInfoTextBold>

                                                <LogInfoText>
                                                    {getLogPersonText(
                                                        showAssessorPerson,
                                                        taskData.details.name,
                                                        taskData.details.rankName,
                                                        attempt.assessorId,
                                                        assessors,
                                                        attempt.assessorName,
                                                        attempt.assessorRank,
                                                    )}
                                                </LogInfoText>
                                            </LogInfo>

                                            <LogInfo>
                                                <LogInfoText>
                                                    <DateWrapper>
                                                        {formatDateBasedOnPreferences(displayDateUTC, dateFormat)}
                                                    </DateWrapper>
                                                </LogInfoText>

                                                {!!showStatusPill && (
                                                    <StatusInfoWrapper>
                                                        {!!StatusIcon && <StatusIcon />}

                                                        <StatusTextWrapper>
                                                            <LogInfoTextBold $color={LOG_TITLE_COLOR_MAPPER.TEXT_BODY}>
                                                                {t`Status: `}
                                                            </LogInfoTextBold>

                                                            <LogInfoText>{statusLabel}</LogInfoText>
                                                        </StatusTextWrapper>
                                                    </StatusInfoWrapper>
                                                )}
                                            </LogInfo>
                                        </LogInfoWrapper>
                                    </LogHeader>

                                    <LogContent>
                                        {!!ratings &&
                                            !!backgrounds &&
                                            generateUniqueKeys(taskData.taskConfiguration.subTasks).map(
                                                ({ item: task, key: taskKey }, taskIndex) => {
                                                    const learnerRating = attempt.learnerAnswers.find(
                                                        (answer) => answer.order === taskIndex,
                                                    )?.points;

                                                    const assessorRating = attempt.assessorAnswers.find(
                                                        (answer) => answer.order === taskIndex,
                                                    )?.points;

                                                    const selectedRating = isAssessorEvaluation
                                                        ? assessorRating
                                                        : learnerRating;
                                                    const compareRating = isAssessorEvaluation ? learnerRating : null;

                                                    return (
                                                        <TaskWrapper key={taskKey}>
                                                            <Description>
                                                                {`${taskIndex + 1}.`} {task.description}
                                                            </Description>

                                                            <Guideline>{task.guideline}</Guideline>

                                                            <RatingsWrapper>
                                                                {ratings.map((rating, ratingIndex) => (
                                                                    <Rating
                                                                        key={rating}
                                                                        $isInactive={isInactiveRatings}
                                                                        $isSelected={
                                                                            !isInactiveRatings &&
                                                                            selectedRating === ratingIndex + 1
                                                                        }
                                                                        $hasAssessorOrAssesseeSelection={
                                                                            !isInactiveRatings &&
                                                                            compareRating === ratingIndex + 1
                                                                        }
                                                                        $selectedBackground={backgrounds[ratingIndex]}
                                                                    >
                                                                        {rating}
                                                                    </Rating>
                                                                ))}
                                                            </RatingsWrapper>
                                                        </TaskWrapper>
                                                    );
                                                },
                                            )}

                                        {!!taskData.taskConfiguration.referenceText && (
                                            <SectionWrapper>
                                                <Label>{t`Reference`}</Label>

                                                <ReferenceContentWrapper>
                                                    <HtmlInjection text={taskData.taskConfiguration.referenceText} />
                                                </ReferenceContentWrapper>
                                            </SectionWrapper>
                                        )}

                                        {!!attempt.learnerComments && (
                                            <SectionWrapper>
                                                <Label>
                                                    {attempt.isExemption
                                                        ? t`Reason for Exemption`
                                                        : t`Assessee's Comments`}
                                                </Label>

                                                <CommentsContent>{attempt.learnerComments}</CommentsContent>
                                            </SectionWrapper>
                                        )}

                                        {evidenceFileId !== null && (
                                            <SectionWrapper>
                                                <Label>{t`Evidence file`}</Label>

                                                <EvidenceWrapper>
                                                    <BaseInput
                                                        value={attempt.evidenceFileName ?? t`Uploaded evidence`}
                                                        readOnly
                                                    />

                                                    <EvidenceButtonsWrapper>
                                                        <BaseButton disabled>{t`Remove`}</BaseButton>

                                                        <BaseButton
                                                            $customType="outline"
                                                            onClick={() => handleEvidencePreview(evidenceFileId)}
                                                            iconPlacement="start"
                                                            icon={<EyeIcon />}
                                                        />
                                                    </EvidenceButtonsWrapper>
                                                </EvidenceWrapper>
                                            </SectionWrapper>
                                        )}

                                        {!!showAssessorComments && (
                                            <SectionWrapper>
                                                <Label>{t`Assessor's Comments`}</Label>

                                                <CommentsContent>{attempt.assessorComments}</CommentsContent>
                                            </SectionWrapper>
                                        )}
                                    </LogContent>
                                </Log>
                            );
                        })}

                        <PrintWrapper>
                            <BaseButton
                                onClick={handlePrint}
                                iconPlacement="end"
                                icon={<PrinterIcon />}
                                $customType="outline"
                            >
                                {t`Print history`}
                            </BaseButton>
                        </PrintWrapper>
                    </EvaluationLogSection>
                </>
            )}
        </TaskEvaluationLogWrapper>
    );
};
